import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ActionIcon,
  Box,
  Button,
  Drawer,
  Group,
  Menu,
  Modal,
  NumberInput,
  Select,
  Text,
  TextInput,
} from '@mantine/core';
import {
  IconDeviceFloppy,
  IconDots,
  IconLayoutSidebarRight,
  IconPictureInPicture,
  IconSearch,
  IconTrash,
  IconX,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { shellDrawerClassNames } from '../../../../app/shellDrawerClassNames';
import type { LinkSnapshot, TaskSnapshot } from '../../../../stores/types';
import {
  formatDependencyLinkToken,
  type DependencyApiType,
} from '../../../../gantt/dependencyDisplayUtils';
import { validatePredecessorLink } from '../../../../gantt/predecessorValidation';
import { useModalContainer } from '../../../../contexts/ModalContainerContext';
import { PredecessorTaskTree } from './PredecessorTaskTree';
import { GanttChartLoader } from '../GanttChartLoader';
import {
  appendPredecessorToken,
  buildPredecessorTokenForTask,
  parsePredecessorSelections,
  removePredecessorSourceFromInput,
  replacePredecessorSourceInInput,
  updatePredecessorSelectionInInput,
  type PredecessorSelection,
  type PredecessorTreeNode,
} from './predecessorPickerUtils';
import type { PredecessorGridShell } from './predecessorGridShellPreference';

export type PredecessorPickerPresentation = 'drawer' | 'modal' | 'floating';

export interface PredecessorTaskPickerPanelProps {
  opened: boolean;
  onClose: () => void;
  /** Task receiving the predecessors. */
  targetTaskId: string | null;
  tasks: TaskSnapshot[];
  /** Existing dependency links (cycle checks + dimming). */
  links?: LinkSnapshot[];
  /** Current predecessor field text (tokens). */
  currentInput: string;
  /** Apply the full updated predecessor input string. */
  onApplyInput: (input: string) => void;
  resolveWbsCode?: (taskId: string) => string | null;
  title?: string;
  /**
   * Shell: right Drawer / floating panel for grid; Modal stacked above Add Task / Task Details.
   */
  presentation?: PredecessorPickerPresentation;
  /**
   * Grid-only: show the dock / float menu and report shell changes.
   * Ignored when presentation is `modal`.
   */
  onGridShellChange?: (shell: PredecessorGridShell) => void;
}

const DEPENDENCY_TYPE_OPTIONS = [
  { value: 'FS', label: 'FS' },
  { value: 'SS', label: 'SS' },
  { value: 'FF', label: 'FF' },
  { value: 'SF', label: 'SF' },
];

const DEPENDENCY_TYPE_OPTIONS_FULL = [
  { value: 'FS', label: 'FS — Finish to Start' },
  { value: 'SS', label: 'SS — Start to Start' },
  { value: 'FF', label: 'FF — Finish to Finish' },
  { value: 'SF', label: 'SF — Start to Finish' },
];

/** Align with Task Details drawer width. */
const DRAWER_SIZE = 560;
/** Nested above Add Task / Task Details; Select dropdowns use +100. */
const MODAL_Z_INDEX = 1200;
const FLOATING_Z_INDEX = 1000;
const DROPDOWN_Z_INDEX = MODAL_Z_INDEX + 100;
/** Whole modal, including the header — a definite height so the tree can fill the leftover. */
const MODAL_SHELL_HEIGHT = 'min(85vh, 760px)';
const PANEL_WIDTH = 520;
const PANEL_HEIGHT = 560;
const MIN_PANEL_WIDTH = 400;
const MIN_PANEL_HEIGHT = 400;
const HEADER_HEIGHT = 49;
const MIN_PANEL_TOP = 8;

/**
 * Mantine's default scroll wrapper is a fragment, so the viewport height it passes is
 * dropped and Drawer/Modal content keeps `overflow-y: auto`. The tree host then grows
 * with the arborist list. This shell clips header + body to the drawer or modal.
 * The `style` argument is ignored on purpose — it is a 100vh/100dvh guess that does
 * not match the content box.
 */
function PickerShellScroll({ children }: { children?: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: '1 1 auto',
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      {children}
    </div>
  );
}

function clampPanelPosition(
  pos: { x: number; y: number },
  size: { width: number; height: number },
): { x: number; y: number } {
  const maxX = typeof window !== 'undefined' ? Math.max(0, window.innerWidth - size.width) : pos.x;
  const maxY =
    typeof window !== 'undefined'
      ? Math.max(MIN_PANEL_TOP, window.innerHeight - HEADER_HEIGHT)
      : pos.y;
  return {
    x: Math.max(0, Math.min(pos.x, maxX)),
    y: Math.max(MIN_PANEL_TOP, Math.min(pos.y, maxY)),
  };
}

/**
 * Predecessor task-tree picker.
 * Linked list edits type/lag; tree adds or replaces the focused row.
 * Grid → Drawer or floating panel (toggle); Add Task / Task Details → Modal.
 */
export function PredecessorTaskPickerPanel({
  opened,
  onClose,
  targetTaskId,
  tasks,
  links = [],
  currentInput,
  onApplyInput,
  resolveWbsCode,
  title = 'Pick predecessors',
  presentation = 'drawer',
  onGridShellChange,
}: PredecessorTaskPickerPanelProps) {
  const modalContainer = useModalContainer();
  const [searchTerm, setSearchTerm] = useState('');
  const [pendingSourceId, setPendingSourceId] = useState<string | null>(null);
  const [focusedSourceId, setFocusedSourceId] = useState<string | null>(null);
  const [depType, setDepType] = useState<DependencyApiType>('FS');
  const [lag, setLag] = useState(0);
  /** Unsaved type/lag edits keyed by source task id — committed only via Save. */
  const [drafts, setDrafts] = useState<Record<string, { type: DependencyApiType; lag: number }>>(
    {},
  );
  const [applying, setApplying] = useState(false);
  const applyingClearTimerRef = useRef<number | null>(null);
  const prevOpenedRef = useRef(false);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const treeHostObserverRef = useRef<ResizeObserver | null>(null);
  const [treeSize, setTreeSize] = useState({ width: 480, height: 240 });

  const [floatPosition, setFloatPosition] = useState({ x: 120, y: 100 });
  const [floatSize, setFloatSize] = useState({ width: PANEL_WIDTH, height: PANEL_HEIGHT });
  const dragRef = useRef({ isDragging: false, startX: 0, startY: 0, startLeft: 0, startTop: 0 });
  const [isDragging, setIsDragging] = useState(false);

  const showShellToggle =
    Boolean(onGridShellChange) && (presentation === 'drawer' || presentation === 'floating');

  useEffect(() => {
    if (opened && !prevOpenedRef.current) {
      setSearchTerm('');
      setPendingSourceId(null);
      setFocusedSourceId(null);
      setDepType('FS');
      setLag(0);
      setDrafts({});
      setApplying(false);
      setFloatPosition(
        clampPanelPosition(
          { x: 120, y: 100 },
          { width: PANEL_WIDTH, height: PANEL_HEIGHT },
        ),
      );
      setFloatSize({ width: PANEL_WIDTH, height: PANEL_HEIGHT });
      prevOpenedRef.current = true;
      window.requestAnimationFrame(() => searchInputRef.current?.focus());
    }
    if (!opened) prevOpenedRef.current = false;
  }, [opened]);

  useEffect(() => {
    if (!opened) return;
    setPendingSourceId(null);
    setFocusedSourceId(null);
    setDepType('FS');
    setLag(0);
    setDrafts({});
    setSearchTerm('');
  }, [opened, targetTaskId]);

  useEffect(() => {
    return () => {
      if (applyingClearTimerRef.current != null) {
        window.clearTimeout(applyingClearTimerRef.current);
      }
    };
  }, []);

  // The arborist list only scrolls the pixel box we pass it. Measure an absolute
  // fill of the flex slot so that box is the drawer, modal, or floater leftover —
  // never the list's own height (that feedback kept the floater's scrollbar).
  // Do not re-bind on currentInput / focus; that remounts the observer on every Add.
  const setTreeHost = useCallback((node: HTMLDivElement | null) => {
    treeHostObserverRef.current?.disconnect();
    treeHostObserverRef.current = null;
    if (!node) return;
    const measure = () => {
      const width = Math.floor(node.clientWidth);
      const height = Math.floor(node.clientHeight);
      if (width < 80 || height < 40) return;
      setTreeSize((prev) =>
        prev.width === width && prev.height === height ? prev : { width, height },
      );
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(node);
    treeHostObserverRef.current = ro;
  }, []);

  const stopWheelPropagation = useCallback((e: React.WheelEvent) => {
    e.stopPropagation();
  }, []);

  const onMouseDownHeader = useCallback(
    (e: React.MouseEvent) => {
      if ((e.target as HTMLElement).closest('button, input')) {
        return;
      }
      e.preventDefault();
      dragRef.current = {
        isDragging: true,
        startX: e.clientX,
        startY: e.clientY,
        startLeft: floatPosition.x,
        startTop: floatPosition.y,
      };
      setIsDragging(true);
    },
    [floatPosition.x, floatPosition.y],
  );

  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e: MouseEvent) => {
      const d = dragRef.current;
      if (!d.isDragging) return;
      setFloatPosition(
        clampPanelPosition(
          {
            x: d.startLeft + (e.clientX - d.startX),
            y: d.startTop + (e.clientY - d.startY),
          },
          floatSize,
        ),
      );
    };
    const onUp = () => {
      dragRef.current.isDragging = false;
      setIsDragging(false);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [isDragging, floatSize]);

  const shellToggle = showShellToggle ? (
    <Menu
      shadow="md"
      width={220}
      position="bottom-end"
      withinPortal
      zIndex={FLOATING_Z_INDEX + 100}
    >
      <Menu.Target>
        <ActionIcon
          variant="subtle"
          color="gray"
          size="sm"
          aria-label="More options"
          onMouseDown={(e) => e.stopPropagation()}
        >
          <IconDots size={16} />
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Label>Display</Menu.Label>
        {presentation === 'floating' ? (
          <Menu.Item
            leftSection={<IconLayoutSidebarRight size={14} />}
            onClick={() => onGridShellChange?.('drawer')}
          >
            Dock as drawer
          </Menu.Item>
        ) : (
          <Menu.Item
            leftSection={<IconPictureInPicture size={14} />}
            onClick={() => onGridShellChange?.('floating')}
          >
            Open as floating panel
          </Menu.Item>
        )}
      </Menu.Dropdown>
    </Menu>
  ) : null;

  const appliedSelections = useMemo(() => {
    if (!targetTaskId) return [];
    return parsePredecessorSelections(currentInput, tasks, targetTaskId, resolveWbsCode);
  }, [currentInput, tasks, targetTaskId, resolveWbsCode]);

  // Drop drafts once the applied store matches (Save completed) or the link was removed.
  useEffect(() => {
    setDrafts((prev) => {
      const ids = Object.keys(prev);
      if (ids.length === 0) return prev;
      let next: typeof prev | null = null;
      for (const id of ids) {
        const applied = appliedSelections.find((s) => s.sourceTaskId === id);
        const draft = prev[id];
        if (!applied || (applied.type === draft.type && applied.lag === draft.lag)) {
          if (!next) next = { ...prev };
          delete next[id];
        }
      }
      return next ?? prev;
    });
  }, [appliedSelections]);

  const selections = useMemo((): PredecessorSelection[] => {
    return appliedSelections.map((s) => {
      const draft = drafts[s.sourceTaskId];
      if (!draft) return s;
      return {
        ...s,
        type: draft.type,
        lag: draft.lag,
        token: formatDependencyLinkToken(s.displayCode, draft.type, draft.lag),
      };
    });
  }, [appliedSelections, drafts]);

  const selectedSourceIds = useMemo(
    () => new Set(appliedSelections.map((s) => s.sourceTaskId)),
    [appliedSelections],
  );

  useEffect(() => {
    if (focusedSourceId && !selectedSourceIds.has(focusedSourceId)) {
      setFocusedSourceId(null);
    }
  }, [focusedSourceId, selectedSourceIds]);

  const pendingTask = useMemo(
    () => (pendingSourceId ? tasks.find((t) => String(t.id) === pendingSourceId) : null),
    [pendingSourceId, tasks],
  );

  const focusedSelection = useMemo(
    () => selections.find((s) => s.sourceTaskId === focusedSourceId) ?? null,
    [selections, focusedSourceId],
  );

  const isReplaceMode = Boolean(focusedSelection && pendingTask);

  const runApply = useCallback(
    (nextInput: string) => {
      if (applyingClearTimerRef.current != null) {
        window.clearTimeout(applyingClearTimerRef.current);
      }
      setApplying(true);
      // Let the overlay paint before the heavy gantt/store sync.
      window.requestAnimationFrame(() => {
        try {
          onApplyInput(nextInput);
        } finally {
          applyingClearTimerRef.current = window.setTimeout(() => {
            setApplying(false);
            applyingClearTimerRef.current = null;
          }, 120);
        }
      });
    },
    [onApplyInput],
  );

  const handleSelectNode = useCallback(
    (node: PredecessorTreeNode, selectable: boolean, reason?: string) => {
      if (!selectable) {
        if (reason) {
          notifications.show({
            title: 'Invalid predecessor',
            message: reason,
            color: 'red',
          });
        }
        return;
      }
      if (selectedSourceIds.has(node.id) && node.id !== focusedSourceId) {
        notifications.show({
          title: 'Already added',
          message: `${node.label} is already a predecessor.`,
          color: 'yellow',
        });
        return;
      }
      if (focusedSourceId && node.id === focusedSourceId) {
        setPendingSourceId(null);
        return;
      }
      setPendingSourceId(node.id);
      if (!focusedSourceId) {
        setDepType('FS');
        setLag(0);
      } else if (focusedSelection) {
        setDepType(focusedSelection.type);
        setLag(focusedSelection.lag);
      }
    },
    [selectedSourceIds, focusedSourceId, focusedSelection],
  );

  const handleAddOrReplace = useCallback(() => {
    if (!targetTaskId || !pendingTask || applying) return;
    const check = validatePredecessorLink(
      String(pendingTask.id),
      targetTaskId,
      tasks,
      links,
    );
    if (!check.ok) {
      notifications.show({
        title: 'Invalid predecessor',
        message: check.reason,
        color: 'red',
      });
      setPendingSourceId(null);
      return;
    }

    if (focusedSourceId && focusedSelection) {
      const next = replacePredecessorSourceInInput(
        currentInput,
        focusedSourceId,
        pendingTask,
        tasks,
        targetTaskId,
        resolveWbsCode,
      );
      if (next.trim() === currentInput.trim()) {
        notifications.show({
          title: 'Already added',
          message: `${pendingTask.taskName || pendingTask.id} is already a predecessor.`,
          color: 'yellow',
        });
        return;
      }
      runApply(next);
      setFocusedSourceId(String(pendingTask.id));
      setPendingSourceId(null);
      return;
    }

    const token = buildPredecessorTokenForTask(pendingTask, depType, lag);
    const next = appendPredecessorToken(
      removePredecessorSourceFromInput(
        currentInput,
        String(pendingTask.id),
        tasks,
        targetTaskId,
        resolveWbsCode,
      ),
      token,
    );
    runApply(next);
    setPendingSourceId(null);
  }, [
    targetTaskId,
    pendingTask,
    depType,
    lag,
    currentInput,
    tasks,
    links,
    resolveWbsCode,
    runApply,
    focusedSourceId,
    focusedSelection,
    applying,
  ]);

  const handleRemove = useCallback(
    (sourceTaskId: string) => {
      if (!targetTaskId || applying) return;
      setDrafts((prev) => {
        if (!prev[sourceTaskId]) return prev;
        const next = { ...prev };
        delete next[sourceTaskId];
        return next;
      });
      const next = removePredecessorSourceFromInput(
        currentInput,
        sourceTaskId,
        tasks,
        targetTaskId,
        resolveWbsCode,
      );
      runApply(next);
      if (pendingSourceId === sourceTaskId) setPendingSourceId(null);
      if (focusedSourceId === sourceTaskId) setFocusedSourceId(null);
    },
    [
      targetTaskId,
      applying,
      currentInput,
      tasks,
      resolveWbsCode,
      runApply,
      pendingSourceId,
      focusedSourceId,
    ],
  );

  const handleDraftChange = useCallback(
    (sourceTaskId: string, patch: { type?: DependencyApiType; lag?: number }) => {
      const applied = appliedSelections.find((s) => s.sourceTaskId === sourceTaskId);
      if (!applied) return;
      setDrafts((prev) => {
        const current = prev[sourceTaskId] ?? { type: applied.type, lag: applied.lag };
        const nextPatch = {
          type: patch.type ?? current.type,
          lag: patch.lag !== undefined ? patch.lag : current.lag,
        };
        // If draft matches applied, clear it.
        if (nextPatch.type === applied.type && nextPatch.lag === applied.lag) {
          if (!prev[sourceTaskId]) return prev;
          const next = { ...prev };
          delete next[sourceTaskId];
          return next;
        }
        return { ...prev, [sourceTaskId]: nextPatch };
      });
    },
    [appliedSelections],
  );

  const handleSaveRow = useCallback(
    (sourceTaskId: string) => {
      if (!targetTaskId || applying) return;
      const draft = drafts[sourceTaskId];
      if (!draft) return;
      const next = updatePredecessorSelectionInInput(
        currentInput,
        sourceTaskId,
        draft,
        tasks,
        targetTaskId,
        resolveWbsCode,
      );
      runApply(next);
    },
    [targetTaskId, applying, drafts, currentInput, tasks, resolveWbsCode, runApply],
  );

  const handleDiscardRow = useCallback((sourceTaskId: string) => {
    setDrafts((prev) => {
      if (!prev[sourceTaskId]) return prev;
      const next = { ...prev };
      delete next[sourceTaskId];
      return next;
    });
  }, []);

  const body =
    opened && targetTaskId ? (
      <Box
        style={{
          position: 'relative',
          // height 0 + flex-grow: fill the shell, and do not grow with the tree.
          flex: '1 1 auto',
          height: 0,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          padding: 12,
          gap: 10,
          overflow: 'hidden',
        }}
      >
        {/* Linked predecessors — editable */}
        <Box
          style={{
            flexShrink: 0,
            maxHeight: '34%',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            border: '1px solid var(--mantine-color-default-border)',
            borderRadius: 8,
            overflow: 'hidden',
          }}
        >
          <Group justify="space-between" wrap="nowrap" px="xs" pt={8} pb={4} gap="xs">
            <Text
              size="xs"
              fw={600}
              c="dimmed"
              tt="uppercase"
              style={{ letterSpacing: '0.04em' }}
            >
              Linked predecessors
            </Text>
            <Button
              size="compact-xs"
              variant="subtle"
              color="gray"
              disabled={applying || !focusedSourceId}
              style={{
                // Keep header height stable whether or not a row is focused.
                visibility: focusedSourceId ? 'visible' : 'hidden',
                pointerEvents: focusedSourceId ? 'auto' : 'none',
              }}
              onClick={() => {
                setFocusedSourceId(null);
                setPendingSourceId(null);
                setDepType('FS');
                setLag(0);
              }}
            >
              Clear selection
            </Button>
          </Group>
          {selections.length === 0 ? (
            <Text size="xs" c="dimmed" px="xs" pb="sm">
              No predecessors yet — pick a task below, set type and lag/lead, then Add.
            </Text>
          ) : (
            <Box style={{ overflow: 'auto', flex: 1, minHeight: 0 }} onWheel={stopWheelPropagation}>
              {selections.map((s) => {
                const focused = focusedSourceId === s.sourceTaskId;
                const dirty = Boolean(drafts[s.sourceTaskId]);
                return (
                  <Group
                    key={s.sourceTaskId}
                    gap={6}
                    wrap="nowrap"
                    align="flex-start"
                    px="xs"
                    py={6}
                    style={{
                      borderTop: '1px solid var(--mantine-color-default-border)',
                      background: dirty
                        ? 'color-mix(in srgb, var(--mantine-color-yellow-5) 14%, transparent)'
                        : focused
                          ? 'color-mix(in srgb, var(--mantine-color-brand-6) 12%, transparent)'
                          : undefined,
                    }}
                  >
                    <Box
                      style={{
                        flex: 1,
                        minWidth: 0,
                        cursor: applying ? 'default' : 'pointer',
                      }}
                      onClick={() => {
                        if (applying) return;
                        if (focused) {
                          setFocusedSourceId(null);
                          setPendingSourceId(null);
                          setDepType('FS');
                          setLag(0);
                          return;
                        }
                        setFocusedSourceId(s.sourceTaskId);
                        setPendingSourceId(null);
                        setDepType(s.type);
                        setLag(s.lag);
                      }}
                    >
                      <Text size="xs" fw={500} style={{ lineHeight: 1.35, wordBreak: 'break-word' }}>
                        {s.taskName || s.displayCode || s.sourceTaskId}
                      </Text>
                      {s.displayCode && s.taskName ? (
                        <Text size="xs" c="dimmed" style={{ fontSize: 11, lineHeight: 1.3 }}>
                          {s.displayCode}
                          {dirty ? ' · unsaved' : ''}
                        </Text>
                      ) : dirty ? (
                        <Text size="xs" c="dimmed" style={{ fontSize: 11 }}>
                          Unsaved changes
                        </Text>
                      ) : null}
                    </Box>
                    <Select
                      size="xs"
                      w={72}
                      data={DEPENDENCY_TYPE_OPTIONS}
                      value={s.type}
                      disabled={applying}
                      allowDeselect={false}
                      onChange={(v) => {
                        if (v === 'SS' || v === 'FF' || v === 'SF' || v === 'FS') {
                          handleDraftChange(s.sourceTaskId, { type: v });
                        }
                      }}
                      aria-label={`Dependency type for ${s.displayCode}`}
                      comboboxProps={{ withinPortal: true, zIndex: DROPDOWN_Z_INDEX }}
                      styles={{ root: { marginTop: 1 } }}
                    />
                    <NumberInput
                      size="xs"
                      w={70}
                      value={s.lag}
                      disabled={applying}
                      onChange={(v) => {
                        const n = typeof v === 'number' ? v : Number(v);
                        handleDraftChange(s.sourceTaskId, {
                          lag: Number.isFinite(n) ? Math.trunc(n) : 0,
                        });
                      }}
                      aria-label={`Lag or lead for ${s.displayCode}`}
                      styles={{ root: { marginTop: 1 } }}
                    />
                    {/* Fixed-width slot so Save/Discard never shove type/lag left. */}
                    <Group
                      gap={4}
                      wrap="nowrap"
                      justify="flex-end"
                      w={64}
                      style={{ flexShrink: 0, marginTop: 1 }}
                    >
                      {dirty ? (
                        <>
                          <ActionIcon
                            size="sm"
                            variant="filled"
                            color="brand"
                            disabled={applying}
                            aria-label={`Save ${s.displayCode}`}
                            onClick={() => handleSaveRow(s.sourceTaskId)}
                          >
                            <IconDeviceFloppy size={14} />
                          </ActionIcon>
                          <ActionIcon
                            size="sm"
                            variant="subtle"
                            color="gray"
                            disabled={applying}
                            aria-label={`Discard changes to ${s.displayCode}`}
                            onClick={() => handleDiscardRow(s.sourceTaskId)}
                          >
                            <IconX size={14} />
                          </ActionIcon>
                        </>
                      ) : (
                        <ActionIcon
                          size="sm"
                          variant="subtle"
                          color="red"
                          disabled={applying}
                          aria-label={`Remove ${s.token}`}
                          onClick={() => handleRemove(s.sourceTaskId)}
                        >
                          <IconTrash size={14} />
                        </ActionIcon>
                      )}
                    </Group>
                  </Group>
                );
              })}
            </Box>
          )}
        </Box>

        <TextInput
          ref={searchInputRef}
          size="xs"
          placeholder="Search Sr No, name, or WBS…"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.currentTarget.value)}
          leftSection={<IconSearch size={14} />}
          rightSection={
            searchTerm ? (
              <ActionIcon
                size="sm"
                variant="subtle"
                aria-label="Clear search"
                onClick={() => setSearchTerm('')}
              >
                <IconX size={12} />
              </ActionIcon>
            ) : null
          }
          style={{ flexShrink: 0 }}
        />

        <Box
          onWheel={stopWheelPropagation}
          style={{
            // height 0 + flex-grow: leftover inside the shell only. An explicit
            // arborist height must not stretch the drawer, modal, or floater.
            flex: '1 1 auto',
            height: 0,
            minHeight: 0,
            position: 'relative',
            border: '1px solid var(--mantine-color-default-border)',
            borderRadius: 8,
            overflow: 'hidden',
          }}
        >
          <div ref={setTreeHost} style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
            {treeSize.height > 0 ? (
              <PredecessorTaskTree
                tasks={tasks}
                tasksAlreadySorted
                targetTaskId={targetTaskId}
                searchTerm={searchTerm}
                selectedSourceId={pendingSourceId}
                selectedSourceIds={selectedSourceIds}
                links={links}
                width={treeSize.width}
                height={treeSize.height}
                onSelectNode={handleSelectNode}
              />
            ) : null}
          </div>
        </Box>

        <div style={{ flexShrink: 0 }}>
          <Group
            gap="sm"
            wrap="nowrap"
            align="flex-end"
            p="xs"
            style={{
              border: '1px solid var(--mantine-color-default-border)',
              borderRadius: 8,
              background:
                'color-mix(in srgb, var(--mantine-color-default-border) 20%, var(--mantine-color-body))',
            }}
          >
            <Box style={{ flex: 1, minWidth: 0 }}>
              <Text size="xs" c="dimmed" mb={4} truncate>
                {pendingTask
                  ? isReplaceMode
                    ? `Replace with: ${buildPredecessorTokenForTask(pendingTask, focusedSelection!.type, focusedSelection!.lag).replace(/(FS|SS|FF|SF).*$/, '') || pendingTask.id}`
                    : `Add: ${buildPredecessorTokenForTask(pendingTask, depType, lag).replace(/(FS|SS|FF|SF).*$/, '') || pendingTask.id}`
                  : focusedSelection
                    ? 'Selected for replace — click the row again or Clear selection to add instead'
                    : 'Select a task in the tree'}
              </Text>
              {!isReplaceMode ? (
                <Group gap={8} wrap="nowrap" grow>
                  <Select
                    size="xs"
                    data={DEPENDENCY_TYPE_OPTIONS_FULL}
                    value={depType}
                    disabled={!pendingTask}
                    onChange={(v) => {
                      if (v === 'SS' || v === 'FF' || v === 'SF' || v === 'FS') setDepType(v);
                    }}
                    allowDeselect={false}
                    aria-label="Dependency type"
                    comboboxProps={{ withinPortal: true, zIndex: DROPDOWN_Z_INDEX }}
                  />
                  <NumberInput
                    size="xs"
                    label={undefined}
                    placeholder="Lag / lead"
                    value={lag}
                    disabled={!pendingTask}
                    onChange={(v) => {
                      const n = typeof v === 'number' ? v : Number(v);
                      setLag(Number.isFinite(n) ? Math.trunc(n) : 0);
                    }}
                    aria-label="Lag or lead days (negative = lead)"
                  />
                </Group>
              ) : (
                <Text size="xs" c="dimmed">
                  Type and lag stay as on the selected row ({focusedSelection!.type}
                  {focusedSelection!.lag !== 0
                    ? focusedSelection!.lag > 0
                      ? `+${focusedSelection!.lag}`
                      : String(focusedSelection!.lag)
                    : ''}
                  ). Edit them in the list above.
                </Text>
              )}
            </Box>
            <Button size="xs" disabled={!pendingTask || applying} onClick={handleAddOrReplace}>
              {isReplaceMode ? 'Replace' : 'Add'}
            </Button>
          </Group>
        </div>

        {applying ? (
          <Box
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              background: 'color-mix(in srgb, var(--mantine-color-body) 72%, transparent)',
            }}
            aria-live="polite"
            aria-busy="true"
          >
            <GanttChartLoader size="sm" />
            <Text size="sm" fw={500}>
              Updating schedule…
            </Text>
          </Box>
        ) : null}
      </Box>
    ) : null;

  if (presentation === 'modal') {
    return (
      <Modal
        opened={opened && !!targetTaskId}
        onClose={onClose}
        title={
          <Text fw={700} size="md">
            {title}
          </Text>
        }
        centered
        size="lg"
        radius="md"
        padding={0}
        zIndex={MODAL_Z_INDEX}
        closeOnClickOutside={!applying}
        closeOnEscape={!applying}
        scrollAreaComponent={PickerShellScroll}
        portalProps={modalContainer ? { target: modalContainer } : undefined}
        styles={{
          content: {
            display: 'flex',
            flexDirection: 'column',
            height: MODAL_SHELL_HEIGHT,
            maxHeight: '85vh',
            overflow: 'hidden',
          },
          header: {
            flexShrink: 0,
            padding: '12px 16px',
            borderBottom: '1px solid var(--mantine-color-default-border)',
            marginBottom: 0,
          },
          body: {
            flex: '1 1 auto',
            height: 0,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            padding: 0,
            overflow: 'hidden',
          },
        }}
      >
        {body}
      </Modal>
    );
  }

  if (presentation === 'floating') {
    if (!opened || !targetTaskId) return null;
    return createPortal(
      <div
        role="dialog"
        aria-label={title}
        onWheel={stopWheelPropagation}
        style={{
          position: 'fixed',
          left: floatPosition.x,
          top: floatPosition.y,
          width: floatSize.width,
          height: floatSize.height,
          zIndex: FLOATING_Z_INDEX,
          display: 'flex',
          flexDirection: 'column',
          background: 'var(--mantine-color-body)',
          border: '1px solid var(--mantine-color-default-border)',
          borderRadius: 12,
          boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
          overflow: 'hidden',
          minWidth: MIN_PANEL_WIDTH,
          minHeight: MIN_PANEL_HEIGHT,
        }}
      >
        <Group
          justify="space-between"
          wrap="nowrap"
          px="sm"
          gap="xs"
          style={{
            height: HEADER_HEIGHT,
            borderBottom: '1px solid var(--mantine-color-default-border)',
            cursor: isDragging ? 'grabbing' : 'grab',
            userSelect: 'none',
            flexShrink: 0,
          }}
          onMouseDown={onMouseDownHeader}
        >
          <Text size="sm" fw={700} truncate style={{ minWidth: 0, flex: 1 }}>
            {title}
          </Text>
          {shellToggle}
          <ActionIcon variant="subtle" color="gray" onClick={onClose} aria-label="Close">
            <IconX size={16} />
          </ActionIcon>
        </Group>
        {body}
        <div
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            const startX = e.clientX;
            const startY = e.clientY;
            const startW = floatSize.width;
            const startH = floatSize.height;
            const onMove = (ev: MouseEvent) => {
              setFloatSize({
                width: Math.max(MIN_PANEL_WIDTH, startW + (ev.clientX - startX)),
                height: Math.max(MIN_PANEL_HEIGHT, startH + (ev.clientY - startY)),
              });
            };
            const onUp = () => {
              window.removeEventListener('mousemove', onMove);
              window.removeEventListener('mouseup', onUp);
            };
            window.addEventListener('mousemove', onMove);
            window.addEventListener('mouseup', onUp);
          }}
          style={{
            position: 'absolute',
            right: 0,
            bottom: 0,
            width: 14,
            height: 14,
            cursor: 'nwse-resize',
          }}
          aria-hidden
        />
      </div>,
      document.body,
    );
  }

  return (
    <Drawer
      opened={opened && !!targetTaskId}
      onClose={onClose}
      title={
        <Group gap="sm" wrap="nowrap" justify="space-between" style={{ width: '100%', minWidth: 0 }}>
          <Text fw={600} size="sm" truncate style={{ minWidth: 0, flex: 1 }}>
            {title}
          </Text>
          {shellToggle}
        </Group>
      }
      position="right"
      size={DRAWER_SIZE}
      withOverlay={false}
      trapFocus={false}
      lockScroll={false}
      closeOnClickOutside={false}
      closeOnEscape={!applying}
      classNames={shellDrawerClassNames}
      scrollAreaComponent={PickerShellScroll}
      styles={{
        content: {
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          maxHeight: '100%',
          overflow: 'hidden',
          boxShadow:
            '-18px 0 36px rgba(15, 23, 42, 0.18), -4px 0 10px rgba(15, 23, 42, 0.12)',
          borderLeft: '1px solid var(--mantine-color-default-border)',
        },
        header: {
          flexShrink: 0,
        },
        title: {
          flex: 1,
          marginRight: 8,
        },
        body: {
          flex: '1 1 auto',
          height: 0,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          padding: 0,
          overflow: 'hidden',
        },
      }}
    >
      {body}
    </Drawer>
  );
}
